import MitraAssistant from "./components/MitraAssistant/MitraAssistant";
import React, { useContext, useMemo, lazy, Suspense, useEffect, useRef} from "react";
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthProvider, AuthContext } from "./context/AuthContext";
import { BroadcastProvider } from "./context/BroadcastContext";
import { ApprovalProvider } from './context/ApprovalContext';
import { registerForPushNotifications } from './serviceWorkerRegistration';
import EmailMarketingDashboard from './components/EmailMarketing/EmailMarketingDashboard';
import EmailReportPage from './components/EmailMarketing/EmailReportPage';
import ExcelUploader from './components/EmailMarketing/ExcelUploader';
import AuthGuard from "./components/AuthGuard";

// Escalation Management Components
const EscalationDashboard = lazy(() => import("./components/Escalation/EscalationDashboard"));
const ComplaintList = lazy(() => import("./components/Escalation/ComplaintList"));
const ComplaintForm = lazy(() => import("./components/Escalation/ComplaintForm"));
const ComplaintDetail = lazy(() => import("./components/Escalation/ComplaintDetail"));

// Lazy load components
const LoginPage = lazy(() => import("./components/LoginPage"));
const MitraCareDatabase = lazy(() => import("./components/MitraCareDatabase"));
const MitraCareAITest = lazy(() => import("./components/MitraCareAITest"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const HodDashboard = lazy(() => import("./components/HodDashboard"));
const DealerDashboard = lazy(() => import("./components/DealerDashboard"));
const PatientDashboard = lazy(() => import("./components/PatientDashboard"));
const PatientRepeatOrders = lazy(() => import("./components/PatientRepeatOrders"));
const CmDashboard = lazy(() => import("./components/CmDashboard"));
const NcmDashboard = lazy(() => import("./components/NcmDashboard"));
const RcmDashboard = lazy(() => import("./components/RcmDashboard"));
const RsmDashboard = lazy(() => import("./components/RsmDashboard"));
const CustomercareDashboard = lazy(() => import("./components/CustomercareDashboard"));
const DoctorDashboard = lazy(() => import("./components/DoctorDashboard"));
const AddUser = lazy(() => import("./components/AddUser"));
const AddDealer = lazy(() => import("./components/AddDealer"));
const AddClinicalManager = lazy(() => import("./components/AddClinicalManager"));
const AddRCM = lazy(() => import("./components/AddRCM"));
const AddRSM = lazy(() => import("./components/AddRSM"));
const AddSM = lazy(() => import("./components/AddSM"));
const AddDoctor = lazy(() => import("./components/AddDoctor"));
const AddHospital = lazy(() => import("./components/AddHospital"));
const CreateOrder = lazy(() => import("./components/PDOrderForm"));
const CreateInfection = lazy(() => import("./components/CreateInfection"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const ChangePassword = lazy(() => import("./components/ChangePassword"));
const NewBroadcast = lazy(() => import("./components/NewBroadcast"));
const PatientList = lazy(() => import("./components/PatientList"));
const DealerList = lazy(() => import("./components/DealerList"));
const CmList = lazy(() => import("./components/CmList"));
const RcmList = lazy(() => import("./components/RcmList"));
const DoctorList = lazy(() => import("./components/DoctorList"));
const RcmApprovals = lazy(() => import("./components/RcmApprovals"));
const RcmInfectionApprovals = lazy(() => import("./components/RcmInfectionApprovals"));
const MigrationList = lazy(() => import("./components/RcmMigrationList"));
const PDcareApprovals = lazy(() => import("./components/PDcareApprovals"));
const RsmList = lazy(() => import("./components/RsmList"));
const SmList = lazy(() => import("./components/SmList"));
const PatientOrderList = lazy(() => import("./components/PatientOrderList"));
const UpdatePatient = lazy(() => import("./components/UpdatePatient"));
const UpdateMigration = lazy(() => import("./components/UpdateMigration"));
const UpdatePatientCatheter = lazy(() => import("./components/UpdatePatientCatheter"));
const UpdatePatientTset = lazy(() => import("./components/UpdatePatientTset"));
const UpdatePatientTrainingStart = lazy(() => import("./components/UpdatePatientTrainingStart"));
const UpdatePatientTrainingEnd = lazy(() => import("./components/UpdatePatientTrainingEnd"));
const UpdatePatientDemo = lazy(() => import("./components/UpdatePatientDemo"));
const UpdatePatientDrop = lazy(() => import("./components/UpdatePatientDrop"));
const UpdatePatientBagType = lazy(() => import("./components/UpdatePatientBagType"));
const CounsellingForm = lazy(() => import("./components/PatientCounsellingForm"));
const BroadcastMessages = lazy(() => import("./components/BroadcastList"));
const DealerOrderList = lazy(() => import("./components/DealerOrderList"));
const UpdateDealerOrder = lazy(() => import("./components/UpdateDealerOrder"));
const UpdatePatientStatus = lazy(() => import("./components/UpdatePatientStatus"));
const RcmApprovePatientDrop = lazy(() => import("./components/RcmApprovePatientDrop"));
const RcmApproveTrainingStart = lazy(() => import("./components/RcmApproveTrainingStart"));
const RcmMigrationApproval = lazy(() => import("./components/RcmMigrationApproval"));
const PDcareApprovePatientDrop = lazy(() => import("./components/PDcareApprovePatientDrop"));
const PDcareApproveDemo = lazy(() => import("./components/PDcareApproveDemo"));
const PDcareApproveTrainingEnd = lazy(() => import("./components/PDcareApproveTrainingEnd"));
const PDcareApproveConsent = lazy(() => import("./components/PDcareApproveConsent"));
const ExcelUpload = lazy(() => import("./components/ExcelUpload"));
const Report = lazy(() => import("./components/Report"));
const DownloadExcelapi = lazy(() => import("./components/DownloadExcelapi"));
const InfectionReport = lazy(() => import("./components/InfectionReport"));
const OpeningClosingBalanceReport = lazy(() => import("./components/OpeningClosingBalanceReport"));
const SurvivalOutcome = lazy(() => import("./components/SurvivalOutcome"));
const TransfersetDateUpdate = lazy(() => import("./components/TransfersetDateUpdate"));
const IndiaMap = lazy(() => import("./components/IndiaMap"));
const PetReport = lazy(() => import("./components/PETReportGenerator"));
const AdequacyReport = lazy(() => import("./components/AdequacyReportGenerator"));
const FocusedReportGenerator = lazy(() => import("./components/FocusedReportGenerator"));
const PatientAssignment = lazy(() => import("./components/PatientAssignment"));
const BulkCMUpdate = lazy(() => import("./components/BulkCMUpdate"));
const BulkPatientFieldUpdater = lazy(() => import("./components/BulkPatientFieldUpdater"));
const ClinicalReports = lazy(() => import("./components/ClinicalReports"));
const RcmApprovalCalender = lazy(() => import("./components/RcmApprovalCalender"));
const ActivityLogs = lazy(() => import("./components/ActivityLogs"));
const DarClinical = lazy(() => import("./components/DarClinical"));
const DarRcm = lazy(() => import("./components/DarRcm"));
const ViewPatientDetails = lazy(() => import("./components/ViewPatientDetails"));
const PetDashboard = lazy(() => import("./components/PetDashboard"));
const AdequacyDashboard = lazy(() => import("./components/AdequacyDashboard"));
const PatientProfile = lazy(() => import("./components/PatientProfile"));
const TestRun = lazy(() => import("./components/TestRun"));
const DoctorsPatient = lazy(() => import("./components/DoctorsPatient"));
const DarReportDownloader = lazy(() => import("./components/DarReportDownloader"));
const PatientBaselineForm = lazy(() => import("./components/PatientBaselineForm"));
const ManageUserRoles = lazy(() => import("./components/ManageUserRoles"));
const SuperAdminDashboardSelector = lazy(() => import("./components/SuperAdminDashboardSelector"));
const CreateOrderByCM = lazy(() => import("./components/CreateOrderByCM"));
const CreateOrderListCM = lazy(() => import("./components/CreateOrderListCM"));
const BagConsumptionForm = lazy(() => import("./components/BagConsumptionForm"));
const BagConsumptionList = lazy(() => import("./components/BagConsumptionList"));
const CatheterInsertion = lazy(() => import("./components/CatheterInsertion"));
const TherapyInitiation = lazy(() => import("./components/TherapyInitiation"));
const CurrentStatusupdate = lazy(() => import("./components/CurrentStatusupdate"));
const TherapyEnd = lazy(() => import("./components/TherapyEnd"));
const TherapyChangeForm = lazy(() => import("./components/TherapyChangeForm"));
const TherapyChangeList = lazy(() => import("./components/TherapyChangeList"));
const YourMigratedPatients = lazy(() => import("./components/YourMigratedPatients"));
const EditPatient = lazy(() => import("./components/EditPatient"));
const EditRCM = lazy(() => import("./components/EditRCM"));

// Informational UI version. Keep this synchronized with the backend version if /check-token is used.
const CURRENT_CODE_VERSION = "2026.08.14.1"; 

const RoleBasedDashboard = () => {
  const { user } = useContext(AuthContext);
  const token = useMemo(() => localStorage.getItem("token"), []);
  
  if (!user && !token) return <Navigate to="/login" replace />;
  if (!user?.role) return <div className="flex justify-center items-center h-screen">Loading session...</div>;

  const role = user.role.toLowerCase();
  switch (role) {
    case "admin": return <Navigate to="/admin-dashboard" />;
    case "superadmin": return <Navigate to="/admin-dashboard" />;
    case "hod": return <Navigate to="/hod-dashboard" />;
    case "dealer": return <Navigate to="/dealer-dashboard" />;
    case "patient": return <Navigate to="/patient-dashboard" />;
    case "cm": return <Navigate to="/cm-dashboard" />;
    case "rcm": return <Navigate to="/rcm-dashboard" />;
    case "ncm": return <Navigate to="/ncm-dashboard" />;
    case "rsm": return <Navigate to="/rsm-dashboard" />;
    case "pdcare": return <Navigate to="/customercare-dashboard" />;
    case "doctor": return <Navigate to="/doctor-dashboard" />;
    case "campaignadmin": return <Navigate to="/email-marketing" />;
    case "campaignreport": return <Navigate to="/email-reports" />;
    default: return <Navigate to="/login" />;
  }
};

const PublicRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="*" element={<Navigate to="/login" replace />} />
  </Routes>
);

const PrivateRoutes = () => {
  const { user } = useContext(AuthContext);
  if (!user) return <Navigate to="/login" replace />;

  return (
    <Routes>
      <Route path="/" element={<RoleBasedDashboard />} />
      <Route path="/change-password" element={<ChangePassword />} />
      
      {/* ============================================ */}
      {/* ESCALATION MANAGEMENT ROUTES - Available for multiple roles */}
      {/* ============================================ */}
      
      {['admin', 'superadmin', 'cm', 'rcm', 'sm', 'rsm'].includes(user.role.toLowerCase()) && (
        <Route path="/mitracare-database" element={<MitraCareDatabase />} />
      )}
      {/* Admin Routes */}
      {['admin', 'superadmin'].includes(user.role) && (
        <>
          <Route path="/admin-dashboard" element={<AdminDashboard />} />
          <Route path="/mitracare-ai-test" element={<MitraCareAITest />} />

          <Route path="/superadmin-dashboard/:dashboardRole" element={<SuperAdminDashboardSelector />} />
          <Route path="/hod-dashboard" element={<HodDashboard />} />
          <Route path="/dealer-dashboard" element={<DealerDashboard />} />
          <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
          {/* Super Admin can review every available role dashboard. */}
          <Route path="/patient-dashboard" element={<PatientDashboard />} />
          <Route path="/cm-dashboard" element={<CmDashboard />} />
          <Route path="/rcm-dashboard" element={<RcmDashboard />} />
          <Route path="/ncm-dashboard" element={<NcmDashboard />} />
          <Route path="/rsm-dashboard" element={<RsmDashboard />} />
          <Route path="/customercare-dashboard" element={<CustomercareDashboard />} />
          <Route path="/my-patient" element={<DoctorsPatient />} />
          <Route path="/add-user" element={<AddUser />} />
          <Route path="/add-dealer" element={<AddDealer />} />
          <Route path="/add-cm" element={<AddClinicalManager />} />
          <Route path="/add-rcm" element={<AddRCM />} />
          <Route path="/add-rsm" element={<AddRSM />} />
          <Route path="/add-sm" element={<AddSM />} />
          <Route path="/add-doctor" element={<AddDoctor />} />
          <Route path="/add-hospital" element={<AddHospital />} />
          <Route path="/new-broadcast" element={<NewBroadcast />} />
          <Route path="/broadcast-message" element={<BroadcastMessages />} />
          <Route path="/patient-list" element={<PatientList />} />
          <Route path="/dealer-list" element={<DealerList />} />
          <Route path="/cm-list" element={<CmList />} />
          <Route path="/rcm-list" element={<RcmList />} />
          <Route path="/rsm-list" element={<RsmList />} />
          <Route path="/sm-list" element={<SmList />} />
          <Route path="/doctor-list" element={<DoctorList />} />
          <Route path="/all-orders" element={<RsmDashboard />} />
          <Route path="/bulkupload" element={<ExcelUpload />} />
          <Route path="/report" element={<Report />} />
          <Route path="/infection-report" element={<InfectionReport />} />
          <Route path="/opening-closing-report" element={<OpeningClosingBalanceReport />} />
          <Route path="/survival-outcome" element={<SurvivalOutcome />} />
          <Route path="/update-status" element={<UpdatePatientStatus />} />
          <Route path="/downloadapi-excel" element={<DownloadExcelapi />} />
          <Route path="/indiamap" element={<IndiaMap />} />
          <Route path="/pet-report" element={<PetReport />} />
          <Route path="/adequacy-report" element={<AdequacyReport />} />
          <Route path="/transferset-date-update" element={<TransfersetDateUpdate />} />
          <Route path="/focused-report-ncm" element={<FocusedReportGenerator />} />
          <Route path="/patient-assignment" element={<PatientAssignment />} />
          <Route path="/cm-bulkupdate" element={<BulkCMUpdate />} />
          <Route path="/patientfield-bulkupdate" element={<BulkPatientFieldUpdater />} />
          <Route path="/clinical-reports" element={<ClinicalReports />} />
          <Route path="/rcm-calender" element={<RcmApprovalCalender />} />
          <Route path="/activity-logs" element={<ActivityLogs />} />
          <Route path="/dar-clinical" element={<DarReportDownloader />} />
          <Route path="/dar-rcm" element={<DarRcm />} />
          <Route path="/view-patient-details" element={<ViewPatientDetails />} />
          <Route path="/pet-dashboard" element={<PetDashboard />} />
          <Route path="/adequacy-dashboard" element={<AdequacyDashboard />} />
          <Route path="/email-marketing" element={<EmailMarketingDashboard />} />
          <Route path="/email-reports" element={<EmailReportPage />} />
          <Route path="/rcm-approvals" element={<RcmApprovals />} />
          <Route path="/rcm-approve-trainingstart" element={<RcmApproveTrainingStart />} />
          <Route path="/test-run" element={<TestRun />} />
          <Route path="/excel-uploader" element={<ExcelUploader />} />
          <Route path="/baseline-form" element={<PatientBaselineForm />} />
          <Route path="/manage-user" element={<ManageUserRoles />} />
          <Route path="/govt-listorder" element={<CreateOrderListCM />} />
          <Route path="/bag-consumptionlist" element={<BagConsumptionList />} />
          <Route path="/catheter-insertion" element={<CatheterInsertion />} />
          <Route path="/therapy-initiation" element={<TherapyInitiation />} />
          <Route path="/current-statusupdate" element={<CurrentStatusupdate />} />
          <Route path="/therapy-end" element={<TherapyEnd />} />
          <Route path="/therapy-change" element={<TherapyChangeForm />} />
          <Route path="/therapy-change-report" element={<TherapyChangeList />} />
          <Route path="/edit-patient" element={<EditPatient />} />
          
          {/* Escalation Routes for Admin - Wrapped with AuthGuard */}
          <Route path="/escalation/dashboard" element={<AuthGuard><EscalationDashboard /></AuthGuard>} />
          <Route path="/escalation/complaints" element={<AuthGuard><ComplaintList /></AuthGuard>} />
          <Route path="/escalation/complaints/new" element={<AuthGuard><ComplaintForm /></AuthGuard>} />
          <Route path="/escalation/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
        </>
      )}

      {/* HOD Routes */}
      {user.role === 'hod' && (
        <>
          <Route path="/hod-dashboard" element={<HodDashboard />} />
          <Route path="/view-patient-details" element={<ViewPatientDetails />} />
          <Route path="/all-orders" element={<RsmDashboard />} />
          <Route path="/report" element={<Report />} />
          <Route path="/infection-report" element={<InfectionReport />} />
          <Route path="/opening-closing-report" element={<OpeningClosingBalanceReport />} />
          <Route path="/survival-outcome" element={<SurvivalOutcome />} />
          <Route path="/update-status" element={<UpdatePatientStatus />} />
          <Route path="/downloadapi-excel" element={<DownloadExcelapi />} />
          <Route path="/pet-dashboard" element={<PetDashboard />} />
          <Route path="/adequacy-dashboard" element={<AdequacyDashboard />} />
          <Route path="/therapy-change-report" element={<TherapyChangeList />} />
          
          {/* Escalation Routes for HOD - Wrapped with AuthGuard */}
          <Route path="/escalation/dashboard" element={<AuthGuard><EscalationDashboard /></AuthGuard>} />
          <Route path="/escalation/complaints" element={<AuthGuard><ComplaintList /></AuthGuard>} />
          <Route path="/escalation/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
        </>
      )}

      {/* Dealer Routes */}
      {user.role === 'dealer' && (
        <>
          <Route path="/dealer-dashboard" element={<DealerDashboard />} />
          <Route path="/dealer-orderlist" element={<DealerOrderList />} />
          <Route path="/dealer-order/:id" element={<UpdateDealerOrder />} />
          <Route path="/patient-repeatorders" element={<PatientRepeatOrders />} />
          <Route path="/broadcast-message" element={<BroadcastMessages />} />
        </>
      )}

      {/* Campaign Admin Routes */}
      {user.role === 'campaignadmin' && (
        <>
          <Route path="/email-marketing" element={<EmailMarketingDashboard />} />
          <Route path="/email-reports" element={<EmailReportPage />} />
          <Route path="/excel-uploader" element={<ExcelUploader />} />
        </>
      )}

      {/* Campaign Report Routes */}
      {user.role === 'campaignreport' && (
        <Route path="/email-reports" element={<EmailReportPage />} />
      )}

      {/* Patient Routes */}
      {user.role === 'patient' && (
        <>
          <Route path="/patient-dashboard" element={<PatientDashboard />} />
          <Route path="/your-profile" element={<PatientProfile />} />
          <Route path="/create-order" element={<CreateOrder />} />
          <Route path="/patient-orderlist" element={<PatientOrderList />} />
          <Route path="/broadcast-message" element={<BroadcastMessages />} />
        </>
      )}

      {/* CM Routes */}
      {user.role === 'cm' && (
        <>
          <Route path="/cm-dashboard" element={<CmDashboard />} />
          <Route path="/govt-orders" element={<CreateOrderByCM />} />
          <Route path="/counselling-form" element={<CounsellingForm />} />
          <Route path="/catheter-insertion" element={<CatheterInsertion />} />
          <Route path="/therapy-initiation" element={<TherapyInitiation />} />
          <Route path="/therapy-end" element={<TherapyEnd />} />
          <Route path="/current-statusupdate" element={<CurrentStatusupdate />} />
          <Route path="/update-status" element={<UpdatePatientStatus />} />
          <Route path="/infection-report" element={<InfectionReport />} />
          <Route path="/opening-closing-report" element={<OpeningClosingBalanceReport />} />
          <Route path="/survival-outcome" element={<SurvivalOutcome />} />
          <Route path="/transferset-date-update" element={<TransfersetDateUpdate />} />
          <Route path="/dar-clinical" element={<DarClinical />} />
          <Route path="/pet-report" element={<PetReport />} />
          <Route path="/pet-dashboard" element={<PetDashboard />} />
          <Route path="/adequacy-report" element={<AdequacyReport />} />
          <Route path="/adequacy-dashboard" element={<AdequacyDashboard />} />
          <Route path="/govt-listorder" element={<CreateOrderListCM />} />
          <Route path="/bag-consumptionform" element={<BagConsumptionForm />} />
          <Route path="/bag-consumptionlist" element={<BagConsumptionList />} />
          <Route path="/dar-clinical" element={<DarReportDownloader />} />
          
          {/* Escalation Routes for CM - Wrapped with AuthGuard */}
          <Route path="/escalation/complaints" element={<AuthGuard><ComplaintList /></AuthGuard>} />
          <Route path="/escalation/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
        </>
      )}

      {/* NCM Routes */}
      {user.role === 'ncm' && (
        <>
          <Route path="/ncm-dashboard" element={<NcmDashboard />} />
          <Route path="/dar-clinical" element={<DarReportDownloader />} />
          <Route path="/view-patient-details" element={<ViewPatientDetails />} />
          <Route path="/new-broadcast" element={<NewBroadcast />} />
          <Route path="/broadcast-message" element={<BroadcastMessages />} />
          <Route path="/infection-report" element={<InfectionReport />} />
          <Route path="/opening-closing-report" element={<OpeningClosingBalanceReport />} />
          <Route path="/survival-outcome" element={<SurvivalOutcome />} />
          <Route path="/transferset-date-update" element={<TransfersetDateUpdate />} />
          <Route path="/pet-report" element={<PetReport />} />
          <Route path="/pet-dashboard" element={<PetDashboard />} />
          <Route path="/adequacy-report" element={<AdequacyReport />} />
          <Route path="/adequacy-dashboard" element={<AdequacyDashboard />} />
          <Route path="/focused-report-ncm" element={<FocusedReportGenerator />} />
          <Route path="/clinical-reports" element={<ClinicalReports />} />
          <Route path="/activity-logs" element={<ActivityLogs />} />
          <Route path="/patient-assignment" element={<PatientAssignment />} />
          <Route path="/therapy-change-report" element={<TherapyChangeList />} />
          
          {/* Escalation Routes for NCM - Wrapped with AuthGuard */}
          <Route path="/escalation/dashboard" element={<AuthGuard><EscalationDashboard /></AuthGuard>} />
          <Route path="/escalation/complaints" element={<AuthGuard><ComplaintList /></AuthGuard>} />
          <Route path="/escalation/complaints/new" element={<AuthGuard><ComplaintForm /></AuthGuard>} />
          <Route path="/escalation/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
        </>
      )}

      {/* RCM Routes */}
      {user.role === 'rcm' && (
        <>
          <Route path="/rcm-dashboard" element={<RcmDashboard />} />
          <Route path="/counselling-form" element={<CounsellingForm />} />
          <Route path="/catheter-insertion" element={<CatheterInsertion />} />
          <Route path="/therapy-initiation" element={<TherapyInitiation />} />
          <Route path="/therapy-end" element={<TherapyEnd />} />
          <Route path="/current-statusupdate" element={<CurrentStatusupdate />} />
          <Route path="/therapy-change" element={<TherapyChangeForm />} />
          <Route path="/therapy-change-report" element={<TherapyChangeList />} />
          <Route path="/edit-patient" element={<EditPatient />} />
          <Route path="/rcm-approvals" element={<RcmApprovals />} />
          <Route path="/rcm-approve-infection" element={<RcmInfectionApprovals />} />
          <Route path="/rcm-approve-migration/:migrationId" element={<RcmMigrationApproval />} />
          <Route path="/rcm-migration-list" element={<MigrationList />} />
          <Route path="/update-status" element={<UpdatePatientStatus />} />
          <Route path="/infection-report" element={<InfectionReport />} />
          <Route path="/opening-closing-report" element={<OpeningClosingBalanceReport />} />
          <Route path="/survival-outcome" element={<SurvivalOutcome />} />
          <Route path="/rcm-approve-patient-drop" element={<RcmApprovePatientDrop />} />
          <Route path="/rcm-approve-trainingstart" element={<RcmApproveTrainingStart />} />
          <Route path="/transferset-date-update" element={<TransfersetDateUpdate />} />
          <Route path="/pet-report" element={<PetReport />} />
          <Route path="/pet-dashboard" element={<PetDashboard />} />
          <Route path="/adequacy-report" element={<AdequacyReport />} />
          <Route path="/adequacy-dashboard" element={<AdequacyDashboard />} />
          <Route path="/clinical-reports" element={<ClinicalReports />} />
          <Route path="/dar-rcm" element={<DarRcm />} />
          <Route path="/dar-clinical" element={<DarReportDownloader />} />
          <Route path="/govt-listorder" element={<CreateOrderListCM />} />
          <Route path="/bag-consumptionlist" element={<BagConsumptionList />} />
          <Route path="/edit-rcm" element={<EditRCM />} />
          
          {/* Escalation Routes for RCM - Wrapped with AuthGuard */}
          <Route path="/escalation/dashboard" element={<AuthGuard><EscalationDashboard /></AuthGuard>} />
          <Route path="/escalation/complaints" element={<AuthGuard><ComplaintList /></AuthGuard>} />
          <Route path="/escalation/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
        </>
      )}

      {/* RSM Routes */}
      {user.role === 'rsm' && (
        <>
          <Route path="/rsm-dashboard" element={<RsmDashboard />} />
          <Route path="/govt-listorder" element={<CreateOrderListCM />} />
          
          {/* Escalation Routes for RSM - Wrapped with AuthGuard */}
          <Route path="/escalation/complaints" element={<AuthGuard><ComplaintList /></AuthGuard>} />
          <Route path="/escalation/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
        </>
      )}

      {/* PDCare Routes */}
      {user.role === 'pdcare' && (
        <>
          <Route path="/customercare-dashboard" element={<CustomercareDashboard />} />
          <Route path="/dar-clinical" element={<DarReportDownloader />} />
          <Route path="/pdcare-approvals" element={<PDcareApprovals />} />
          <Route path="/dealer-list" element={<DealerList />} />
          <Route path="/cm-list" element={<CmList />} />
          <Route path="/rcm-list" element={<RcmList />} />
          <Route path="/sm-list" element={<SmList />} />
          <Route path="/rsm-list" element={<RsmList />} />
          <Route path="/add-doctor" element={<AddDoctor />} />
          <Route path="/add-hospital" element={<AddHospital />} />
          <Route path="/doctor-list" element={<DoctorList />} />
          <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
          <Route path="/infection-report" element={<InfectionReport />} />
          <Route path="/opening-closing-report" element={<OpeningClosingBalanceReport />} />
          <Route path="/survival-outcome" element={<SurvivalOutcome />} />
          <Route path="/approve-patient-drop-pdcare" element={<PDcareApprovePatientDrop />} />
          <Route path="/approve-democall-pdcare" element={<PDcareApproveDemo />} />
          <Route path="/approve-trainingend-pdcare" element={<PDcareApproveTrainingEnd />} />
          <Route path="/approve-consent-pdcare" element={<PDcareApproveConsent />} />
          <Route path="/transferset-date-update" element={<TransfersetDateUpdate />} />
          <Route path="/downloadapi-excel" element={<DownloadExcelapi />} />
          <Route path="/clinical-reports" element={<ClinicalReports />} />
          <Route path="/activity-logs" element={<ActivityLogs />} />
          <Route path="/add-user" element={<AddUser />} />
          <Route path="/email-reports" element={<EmailReportPage />} />
          <Route path="/therapy-change-report" element={<TherapyChangeList />} />
          
          {/* Escalation Routes for PDCare - Wrapped with AuthGuard */}
          <Route path="/escalation/dashboard" element={<AuthGuard><EscalationDashboard /></AuthGuard>} />
          <Route path="/escalation/complaints" element={<AuthGuard><ComplaintList /></AuthGuard>} />
          <Route path="/escalation/complaints/new" element={<AuthGuard><ComplaintForm /></AuthGuard>} />
          <Route path="/escalation/complaints/:id" element={<AuthGuard><ComplaintDetail /></AuthGuard>} />
        </>
      )}

      {/* Doctor Routes */}
      {user.role === 'doctor' && (
        <>
          <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
          <Route path="/my-patient" element={<DoctorsPatient />} />
          <Route path="/view-patient-details" element={<ViewPatientDetails />} />
          <Route path="/update-patient/:id" element={<UpdatePatient />} />
          <Route path="/patient-list" element={<PatientList />} />
          <Route path="/pet-dashboard" element={<PetDashboard />} />
          <Route path="/adequacy-dashboard" element={<AdequacyDashboard />} />
        </>
      )}

      {/* Common Routes */}
      <Route path="/update-patient/:id" element={<UpdatePatient />} />
      <Route path="/update-patient-catheter" element={<UpdatePatientCatheter />} />
      <Route path="/update-patient-migration" element={<UpdateMigration />} />
      <Route path="/update-patient-tset" element={<UpdatePatientTset />} />
      <Route path="/update-patient-trainingstart" element={<UpdatePatientTrainingStart />} />
      <Route path="/update-patient-democall" element={<UpdatePatientDemo />} />
      <Route path="/update-patient-trainingend" element={<UpdatePatientTrainingEnd />} />
      <Route path="/update-bag-type" element={<UpdatePatientBagType />} />
      <Route path="/update-patient-infections" element={<CreateInfection />} />
      <Route path="/update-patient-drop" element={<UpdatePatientDrop />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AppContent = () => {
  const { user, authAxios } = useContext(AuthContext);
  const location = useLocation();
  const versionCheckPerformed = useRef(false);

  // Production-safe version check.
  // The previous implementation automatically reloaded the page whenever the
  // server version differed from CURRENT_CODE_VERSION. If the backend version
  // was not updated at the same time, this could cause an endless reload loop
  // and Chrome's "Throttling navigation" error.
  useEffect(() => {
    if (!user || versionCheckPerformed.current) return;

    versionCheckPerformed.current = true;

    const checkVersion = async () => {
      try {
        const response = await authAxios.get("/check-token");
        const serverVersion = response?.data?.latest_version;

        if (serverVersion && serverVersion !== CURRENT_CODE_VERSION) {
          console.warn(
            `A newer application version is available. ` +
            `Server: ${serverVersion}, Client: ${CURRENT_CODE_VERSION}. ` +
            `Automatic reload is disabled to prevent a navigation loop.`
          );
        }
      } catch (err) {
        // Version checking must never prevent the application from opening.
        console.error("Version check failed:", err?.message || err);
      }
    };

    checkVersion();
  }, [user, authAxios]);

  // Production deployment safety:
  // Disable the service worker and remove any previously registered worker.
  // This prevents stale cached React chunks from causing ChunkLoadError after
  // a new deployment.
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }
  }, []);

  useEffect(() => {
    if (user && ['admin', 'superadmin'].includes(user.role)) {
      registerForPushNotifications('admin');
    }
  }, [user]);

  return (
    <div key={location.pathname}>
      <Suspense fallback={<div className="flex justify-center items-center h-screen text-xl">Loading...</div>}>
        <ToastContainer position="top-right" autoClose={3000} theme="colored" />
        {user ? <PrivateRoutes /> : <PublicRoutes />}
      </Suspense>
    </div>
  );
};

const App = () => (
  <Router>
    <AuthProvider>
      <ApprovalProvider>
        <BroadcastProvider>
          <AppContent />
          <MitraAssistant />
        </BroadcastProvider>
      </ApprovalProvider>
    </AuthProvider>
  </Router>
);

export default App;
